import React, { useState, useEffect, useMemo } from 'react';
import useFetch from '../../../services/useFetch.js';
import { Box, Button, Stack, Typography } from '@mui/material';
import CustomItemDialog from './CustomItemDialog.jsx';
import ItemMakeupPanel from './ItemMakeupPanel.jsx';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import SnackbarEx from '../../Helpers/SnackbarEx.jsx';
import { currencyFormatter } from '../../Helpers/CurrencyFormat.js';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditCustomItem from './bulkCustomItem/EditCustomItem.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDelete from '../HelperDialogs/ConfirmDelete.jsx';
import DeleteData from '../../../services/DeleteData.js';
import { dataGridSx } from '../../ui/dataGridStyles.js';
import { tokens } from '../../../theme.js';

export default function SpecialtyMenu() {
  const [reloadData, setReloadData] = useState(false);
  const [row, setRow] = useState([]);
  const [col, setCol] = useState(null);
  const [selItem, setSelItem] = useState(null);
  const [selIngrIdArr, setSelIngrIdArr] = useState([]);
  const [makeupOpen, setMakeupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [openAddNewItem, setOpenAddNewItem] = useState(false);
  const [openEditItem, setOpenEditItem] = useState(false);
  const [openTestSnack, setOpenTestSnack] = useState({
    color: '',
    message: '',
    open: false,
  });

  const { data: customMenuData, load } = useFetch('api/custom-menu-item', reloadData);

  const selectedItem = useMemo(() => {
    if (selItem == null || !customMenuData) return null;
    return customMenuData.find((e) => e.itemId === selItem) || null;
  }, [customMenuData, selItem]);

  useEffect(() => {
    setData();
  }, [customMenuData]);

  const setData = () => {
    setRow(
      customMenuData?.map((e) => ({
        id: e.itemId,
        iName: e.itemName,
        category: e.category,
        itemType: e.type,
        sittingInv: e.sellableInventory,
        productionCost: e.productionCost,
        prodAging: e?.ageFactor ?? 0,
        ingredientCount: Array.isArray(e.ingredients) ? e.ingredients.length : 0,
      })) ?? [],
    );
    setCol([
      { field: 'iName', headerName: 'Item', flex: 1.4, minWidth: 160 },
      { field: 'category', headerName: 'Category', flex: 1, minWidth: 120 },
      {
        field: 'itemType',
        headerName: 'Type',
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            {params.value === 'bulkMenuItem' ? 'Bulk menu item' : 'Custom ingredient'}
          </span>
        ),
      },
      {
        field: 'ingredientCount',
        headerName: 'Ingredients',
        flex: 0.7,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'productionCost',
        headerName: 'Cost / unit',
        flex: 0.85,
        minWidth: 110,
        valueFormatter: ({ value }) => (value != null ? currencyFormatter(value, 2) : '—'),
      },
      {
        field: 'sittingInv',
        headerName: 'On hand',
        flex: 0.7,
        minWidth: 90,
      },
    ]);
  };

  const refreshPage = () => {
    setReloadData(true);
    setTimeout(() => setReloadData(false), 200);
  };

  const handlePopupClose = async (val) => {
    if (val === 'success') {
      setOpenAddNewItem(false);
      refreshPage();
      setOpenTestSnack({ color: 'success', message: 'Menu item added!', open: true });
    } else if (val === 'edit') {
      setOpenEditItem(false);
      refreshPage();
      setOpenTestSnack({ color: 'success', message: 'Menu item updated!', open: true });
    } else if (val === 'error') {
      setOpenTestSnack({ color: 'error', message: 'An error occurred.', open: true });
    }
    setOpenAddNewItem(false);
    setOpenEditItem(false);
  };

  const CustomNoRowsOverlay = () => (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
      <Stack spacing={1.5} alignItems="center">
        <Typography sx={{ color: tokens.inkMuted }}>No recipe items yet</Typography>
        <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => setOpenAddNewItem(true)}>
          Add recipe item
        </Button>
      </Stack>
    </Box>
  );

  const deleteCustItem = async (bool) => {
    setDeleteOpen(false);
    if (!bool) return;
    const { success } = await DeleteData('api/delete-custom-item', { itemIds: selIngrIdArr });
    if (success) {
      setSelItem(null);
      setSelIngrIdArr([]);
      setMakeupOpen(false);
      refreshPage();
      setOpenTestSnack({ color: 'success', message: 'Successfully deleted item!', open: true });
    }
  };

  const cellClick = (ids) => {
    const selection = Array.isArray(ids) ? ids : [];
    setSelIngrIdArr(selection);
    if (selection.length === 1) {
      setSelItem(selection[0]);
      setMakeupOpen(true);
    } else {
      setSelItem(null);
      setMakeupOpen(false);
    }
  };

  const closeMakeup = () => setMakeupOpen(false);

  function CustomToolbar() {
    const hasSelection = selIngrIdArr.length > 0;
    const canInspect = selItem != null;
    return (
      <GridToolbarContainer>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 'auto', flexWrap: 'wrap' }}>
          <button
            className={hasSelection ? 'delete-bttn' : 'delete-dis'}
            disabled={!hasSelection}
            onClick={() => setDeleteOpen(true)}
          >
            <DeleteIcon sx={{ color: hasSelection ? tokens.danger : '#a39b92', fontSize: 18 }} />
            <span>Delete</span>
          </button>
          <button
            className={canInspect ? 'recipe-bttn' : 'recipe-dis'}
            disabled={!canInspect}
            onClick={() => setOpenEditItem(true)}
          >
            <EditIcon fontSize="small" sx={{ color: canInspect ? tokens.accent : '#a39b92', fontSize: 18 }} />
            <span>Edit</span>
          </button>
          <button
            className={canInspect ? 'recipe-bttn' : 'recipe-dis'}
            disabled={!canInspect}
            onClick={() => setMakeupOpen(true)}
          >
            <VisibilityOutlinedIcon fontSize="small" sx={{ color: canInspect ? tokens.accent : '#a39b92', fontSize: 18 }} />
            <span>Makeup</span>
          </button>
          <button className="recipe-bttn" onClick={() => setOpenAddNewItem(true)}>
            <AddOutlinedIcon fontSize="small" sx={{ color: tokens.accent, fontSize: 18 }} />
            <span>Add recipe</span>
          </button>
        </Stack>
      </GridToolbarContainer>
    );
  }

  if (load) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {deleteOpen && <ConfirmDelete deleteItem={deleteCustItem} deleteOpen={deleteOpen} />}
      {openAddNewItem && (
        <CustomItemDialog open={openAddNewItem} close={handlePopupClose} />
      )}
      {openEditItem && selItem != null && (
        <EditCustomItem open={openEditItem} close={handlePopupClose} menuId={selItem} />
      )}

      <ItemMakeupPanel
        item={selectedItem}
        open={makeupOpen}
        onClose={closeMakeup}
        onEdit={() => {
          setMakeupOpen(false);
          setOpenEditItem(true);
        }}
      />

      <Box
        sx={{
          border: `1px solid ${tokens.border}`,
          borderRadius: 2,
          backgroundColor: tokens.surface,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 200px)' },
          minHeight: 420,
          mb: 3,
        }}
      >
        <DataGrid
          checkboxSelection
          columns={col || [{ field: 'iName', headerName: 'Item', flex: 1 }]}
          rows={row}
          rowSelectionModel={selIngrIdArr}
          onRowSelectionModelChange={cellClick}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: CustomToolbar,
          }}
          rowHeight={44}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            ...dataGridSx,
            border: 'none',
            height: '100%',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-virtualScroller': { minHeight: 240 },
          }}
        />
      </Box>

      {openTestSnack.open && (
        <SnackbarEx
          color={openTestSnack.color}
          message={openTestSnack.message}
          open={openTestSnack.open}
          close={() => setOpenTestSnack({ color: '', message: '', open: false })}
        />
      )}
    </>
  );
}
